import { getProofreadingManuscriptContent } from '@/lib/api/proofreading';
import type { Manuscript } from '@/lib/models';
import { mapWithConcurrency } from '@/lib/utils/mapWithConcurrency';

export type ManuscriptCharCountEntry = {
  manuscriptKey: string;
  characterCount: number;
};

export type ManuscriptCharacterCountsResult = {
  counts: Record<string, number>;
  failedKeys: string[];
};

/** 原稿本文取得の同時実行上限。作品内原稿数が多いときのリクエスト爆発を防ぐ。 */
export const AI_PROOFREADING_MANUSCRIPT_CONTENT_CONCURRENCY = 5;

/** BE `countProofreadingInputChars` と同じくコードポイント数で数える。 */
export function countProofreadingInputChars(content: string): number {
  return [...content].length;
}

/**
 * 原稿一覧の文字数（コードポイント数）を並列取得する。
 * 校正 REST API（rule-based / AI と同じ原稿解決）を使う。
 * 取得失敗は 0 文字にせず failedKeys で返す。
 * Manuscript メタに characterCount は無いため本文を取得する。同時実行は 5 件まで。
 */
export async function loadManuscriptCharacterCounts(
  novelId: string,
  manuscripts: Manuscript[],
): Promise<ManuscriptCharacterCountsResult> {
  const counts: Record<string, number> = {};
  const failedKeys: string[] = [];

  await mapWithConcurrency(
    manuscripts,
    AI_PROOFREADING_MANUSCRIPT_CONTENT_CONCURRENCY,
    async (manuscript) => {
      const { key } = manuscript;
      if (!key) return;

      try {
        const content = await getProofreadingManuscriptContent(novelId, key, 'content');
        counts[key] = countProofreadingInputChars(content);
      } catch {
        failedKeys.push(key);
      }
    },
  );

  return { counts, failedKeys };
}

export function sumCharacterCounts(
  manuscriptKeys: string[],
  countsByKey: Record<string, number>,
): number {
  return manuscriptKeys.reduce((sum, key) => sum + (countsByKey[key] ?? 0), 0);
}
